<template>
    <div
        class="upload-input-zone"
        :class="{ 'is-drag-over': isDragOver }"
        @dragenter="handleDragEnter"
        @dragover="handleDragOver"
        @dragleave="handleDragLeave"
        @drop="handleDrop"
    >
        <e-upload
            :model-value="null"
            expanded
            :multiple="multiple"
            :disabled="isDisabled"
            drag-drop
            native
            :accept="accept"
            @update:modelValue="handleFileUpdate"
        >
            <section class="section content">
                <div class="has-text-centered">
                    <p class="m-b-md">
                        <e-icon icon="cloud-upload" size="is-large" />
                    </p>

                    <p>
                        {{ translate('drop_your_files_here_or_click_to_upload') }} {{ maxFileSize / (1024 * 1024) }} MB
                        limit per file.
                    </p>
                </div>
            </section>
        </e-upload>
    </div>
</template>

<script>
import { useI18n } from 'vue-i18n';

export default {
    name: 'UploadInput',

    props: {
        multiple: Boolean,

        isDisabled: Boolean,

        maxFileSize: {
            type: Number,
            default: 50 * 1024 * 1024, // 50MB default
        },
        maxFiles: {
            type: Number,
            default: 1,
        },
        accept: {
            type: String,
            default: '',
        },
        allowedFileTypes: {
            type: Array,
            default: () => [],
        },
        fileTypeMessage: {
            type: String,
            default: 'Please upload a supported file type.',
        },
    },

    data() {
        return {
            isDragOver: false,
            dragLeaveTimer: null,
        };
    },

    emits: ['update:modelValue', 'validation-error'],

    setup() {
        const { t: translate } = useI18n();
        return { translate };
    },

    beforeUnmount() {
        if (this.dragLeaveTimer) {
            clearTimeout(this.dragLeaveTimer);
            this.dragLeaveTimer = null;
        }
    },

    methods: {
        isValidFileSize(file) {
            return file.size <= this.maxFileSize;
        },

        isValidFileType(file) {
            // If no allowed file types are specified, skip type validation
            if (!this.allowedFileTypes || this.allowedFileTypes.length === 0) {
                return true;
            }

            // Check if file type is in allowed types
            return this.allowedFileTypes.includes(file.type);
        },

        formatFileSize(bytes) {
            if (bytes === 0) return '0 bytes';

            const bytesInKilobyte = 1024;
            const sizeUnits = ['bytes', 'KB', 'MB', 'GB', 'TB'];

            const unitIndex = Math.floor(Math.log(bytes) / Math.log(bytesInKilobyte));
            const clampedIndex = Math.min(unitIndex, sizeUnits.length - 1);
            const size = (bytes / bytesInKilobyte ** clampedIndex).toFixed(2);

            return `${parseFloat(size)} ${sizeUnits[clampedIndex]}`;
        },

        handleDragEnter(e) {
            e.preventDefault();
            e.stopPropagation();

            if (this.dragLeaveTimer) {
                clearTimeout(this.dragLeaveTimer);
                this.dragLeaveTimer = null;
            }

            this.isDragOver = true;
        },

        handleDragOver(e) {
            e.preventDefault();
            e.stopPropagation();

            if (this.dragLeaveTimer) {
                clearTimeout(this.dragLeaveTimer);
                this.dragLeaveTimer = null;
            }

            this.isDragOver = true;
        },

        handleDragLeave(e) {
            e.preventDefault();
            e.stopPropagation();

            this.dragLeaveTimer = setTimeout(() => {
                this.isDragOver = false;
            }, 100);
        },

        handleDrop(e) {
            e.preventDefault();
            e.stopPropagation();

            this.isDragOver = false;

            if (this.dragLeaveTimer) {
                clearTimeout(this.dragLeaveTimer);
                this.dragLeaveTimer = null;
            }

            const files = Array.from(e.dataTransfer.files);

            if (files.length === 0) return;

            // Process files through handleFileUpdate which validates and processes valid files
            // This ensures valid files are uploaded even if some files are invalid
            this.handleFileUpdate(files);
        },

        handleFileUpdate(files) {
            if (!files) {
                return;
            }

            // Convert to array if single file
            const fileArray = Array.isArray(files) ? files : [files];

            // If empty array, don't process anything
            if (fileArray.length === 0) {
                return;
            }

            const validationMessages = [];

            // Check file types first
            const validTypeFiles = fileArray.filter(file => this.isValidFileType(file));
            const invalidTypeFiles = fileArray.filter(file => !this.isValidFileType(file));

            if (invalidTypeFiles.length > 0) {
                invalidTypeFiles.forEach(file => {
                    const extension = `.${file.name.split('.').pop().toLowerCase()}`;
                    validationMessages.push(`The file "${file.name}" (${extension}) is not a supported file type.`);
                });
                validationMessages.push(this.fileTypeMessage);
            }

            // Check file sizes (only for files with valid types)
            const validFiles = validTypeFiles.filter(file => this.isValidFileSize(file));
            const invalidSizeFiles = validTypeFiles.filter(file => !this.isValidFileSize(file));

            if (invalidSizeFiles.length > 0) {
                invalidSizeFiles.forEach(file => {
                    validationMessages.push(
                        `The file "${file.name}" is too large (${this.formatFileSize(file.size)}).`
                    );
                });
                validationMessages.push(
                    `The maximum file size allowed is ${this.formatFileSize(this.maxFileSize)}. Please try uploading a smaller file.`
                );
            }

            // Check max files limit against valid files only
            if (this.maxFiles > 0 && validFiles.length > this.maxFiles) {
                validationMessages.push(`You can only upload up to ${this.maxFiles} file(s).`);
            }

            // If we have validation messages, emit them as a plain-text array (no HTML)
            if (validationMessages.length > 0) {
                this.$emit('validation-error', validationMessages);
                return;
            }

            // If we get here, all validations passed
            // Emit only the new valid files
            this.$emit('update:modelValue', validFiles);
            this.$emit('validation-error', []); // Clear validation messages
        },

        // Add a method to clear the upload component
        clearUpload() {
            this.$emit('update:modelValue', null);
        },
    },
};
</script>

<style lang="scss" scoped>
.upload-input-zone {
    position: relative;
    transition: all 0.3s ease;
    border-radius: 6px;

    &.is-drag-over {
        background: #f0f8ff;
        border: 2px dashed #3273dc !important;

        .upload {
            opacity: 0.8;
        }
    }
}
</style>
